import React, { useCallback, useEffect, useRef, useState } from "react";
import { ReceiptText } from "lucide-react";
import { AttachPicker, uploadAttachments } from "../Attachments";
import { toast } from "sonner";
import api, { today, formatApiError } from "../../lib/api";
import { Autocomplete } from "../Autocomplete";
import { SignaturePad } from "../SignaturePad";
import { Dialog, DialogContent, DialogHeader, DialogTrigger, DialogFooter } from "../ui/dialog";
import { field, label, pill, SplitRows, DocTitle, TypeChooser, downloadDocPdf, cleanSplits, splitsTotal } from "./shared";
import { CreditAccountPicker } from "./CreditAccountPicker";
import { CapitalPartnerPick, PayerFields, InvoiceModeSection } from "./ReceiptSections";

const blank = () => ({
  date: today(), received_from: "", mobile: "", reference: "",
  party_type: "Credit", vehicle_number: "", invoice_mode: "random", invoice_id: "", invoice_no: "", invoice_date: "", account_name: "",
  splits: [{ method: "cash", amount: "", reference: "" }],
});

const useReceiptLookups = (open, setForm) => {
  const [preview, setPreview] = useState("RCPT-…");
  const [ptypes, setPtypes] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [caps, setCaps] = useState([]);
  useEffect(() => {
    if (!open) return;
    api.get("/vouchers/next-number", { params: { doc_type: "receipt" } }).then((r) => setPreview(r.data.preview)).catch((e) => console.error(e));
    api.get("/receipt-party-types").then((r) => { setPtypes(r.data); setForm((f) => ({ ...f, party_type: f.party_type || r.data[0] || "Credit" })); }).catch((e) => console.error(e));
    api.get("/credit-accounts", { params: { include_all: true } }).then((r) => setAccounts(r.data || [])).catch(() => setAccounts([]));
    api.get("/capital-accounts").then((r) => setCaps(r.data || [])).catch(() => setCaps([]));
  }, [open, setForm]);
  return { preview, ptypes, setPtypes, accounts, setAccounts, caps };
};

// Outstanding invoices for the payer, invoice-number lookup and vehicle → party autofill.
const useReceiptAutofill = (open, form, setForm) => {
  const [invoices, setInvoices] = useState([]);
  const fillFromInvoice = useCallback((d) => setForm((f) => ({
    ...f, received_from: d.party_name || f.received_from, vehicle_number: d.vehicle_number || f.vehicle_number, invoice_balance: d.balance || f.invoice_balance || 0,
    mobile: d.mobile || f.mobile, account_name: d.account_name || "", invoice_date: d.invoice_date || "",
  })), [setForm]);

  useEffect(() => {
    if (open && form.received_from && (form.invoice_mode === "individual" || form.invoice_mode === "bulk")) {
      api.get("/invoices", { params: { party_name: form.received_from } }).then((r) => setInvoices((r.data || []).filter((i) => i.balance > 0))).catch(() => setInvoices([]));
    } else setInvoices([]);
  }, [open, form.received_from, form.invoice_mode]);

  useEffect(() => {
    if (!open || form.invoice_mode !== "individual" || form.invoice_id || !form.invoice_no.trim()) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/invoices/lookup", { params: { invoice_no: form.invoice_no.trim() } });
        fillFromInvoice(data);
        setForm((f) => ({ ...f, splits: [{ method: f.splits[0]?.method || "cash", amount: data.balance ? String(data.balance) : (f.splits[0]?.amount || ""), reference: f.splits[0]?.reference || "" }] }));
      } catch { setForm((f) => ({ ...f, invoice_date: "" })); }
    }, 500);
    return () => clearTimeout(t);
  }, [open, form.invoice_no, form.invoice_mode, form.invoice_id, fillFromInvoice, setForm]);

  useEffect(() => {
    if (!open) return;
    const v = (form.vehicle_number || "").trim();
    const pt = (form.party_type || "").toLowerCase();
    if (!v || pt === "credit" || pt.startsWith("capital")) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/parties/by-vehicle", { params: { vehicle: v } });
        if (data && data.party_name) setForm((f) => ({ ...f, received_from: f.received_from || data.party_name, account_name: f.account_name || data.account_name || "", mobile: f.mobile || data.mobile || "" }));
      } catch (e) { console.debug(e); }
    }, 500);
    return () => clearTimeout(t);
  }, [open, form.vehicle_number, form.party_type, setForm]);

  return { invoices, setInvoices, fillFromInvoice };
};

const PartyTypeSection = ({ form, setForm, ptypes, setPtypes, isCredit, isCapital, caps }) => {
  const [newPtype, setNewPtype] = useState("");
  const addPtype = async () => {
    if (!newPtype.trim()) return;
    try {
      const { data } = await api.post("/receipt-party-types", { name: newPtype.trim() });
      setPtypes(data); setForm((f) => ({ ...f, party_type: newPtype.trim() })); setNewPtype("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div>
      <label className="flex items-center gap-2 text-sm font-medium cursor-pointer mb-2">
        <input data-testid="receiptdoc-post-ledger" type="checkbox" className="h-4 w-4 accent-[hsl(var(--primary))]" checked={isCredit}
          onChange={(e) => setForm({ ...form, party_type: e.target.checked ? "Credit" : (ptypes.find((p) => p.toLowerCase() !== "credit") || "Cash"), account_name: "", received_from: "" })} />
        Post to credit ledger (received against a credit account)
      </label>
      <label className={label}>Party type</label>
      <TypeChooser items={ptypes} value={form.party_type} onPick={(p) => setForm({ ...form, party_type: p })}
        testidOf={(p) => `receiptdoc-ptype-${p.replace(/\s/g, "-").toLowerCase()}`}
        newValue={newPtype} setNewValue={setNewPtype} onAdd={addPtype} addTestid="add-ptype-btn" newTestid="new-ptype" placeholder="Add a party type…"
        extra={caps?.length ? <button type="button" data-testid="receiptdoc-ptype-capital" onClick={() => setForm({ ...form, party_type: "Capital Account", account_name: "", received_from: "" })}
          className={`h-10 px-3 rounded-md text-sm ${pill(isCapital)}`}>Capital Withdrawings</button> : null} />
    </div>
  );
};

export function PaymentReceiptDialog({ onDone, triggerClass, compact }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank());
  const [files, setFiles] = useState([]);
  const cashierRef = useRef(null);
  const receiverRef = useRef(null);
  const { preview, ptypes, setPtypes, accounts, setAccounts, caps } = useReceiptLookups(open, setForm);
  const { invoices, setInvoices, fillFromInvoice } = useReceiptAutofill(open, form, setForm);
  const isCredit = (form.party_type || "").toLowerCase() === "credit";
  const isCapital = (form.party_type || "").toLowerCase().startsWith("capital");

  const save = async () => {
    if (isCredit) {
      if (!(form.account_name || "").trim()) return toast.error("Choose a credit account");
    } else if (!form.received_from) return toast.error("Received from is required");
    const splits = cleanSplits(form.splits);
    if (splits.length === 0) return toast.error("Enter at least one amount");
    if (splits.some((s) => s.method !== "cash" && !s.reference.trim())) return toast.error("Add a reference for non-cash methods");
    if (!isCredit && form.invoice_mode === "individual" && !form.invoice_id && !form.invoice_no.trim()) return toast.error("Select or type an invoice to pay");
    const cashier_signature = cashierRef.current?.getData() || "";
    const receiver_signature = receiverRef.current?.getData() || "";
    if (!cashier_signature || !receiver_signature) return toast.error("Both signatures are required");
    try {
      const { data } = await api.post("/payment-receipts", {
        date: form.date, received_from: isCredit ? form.account_name : form.received_from, mobile: isCredit ? "" : form.mobile,
        party_type: form.party_type, account_name: form.account_name, vehicle_number: isCredit ? "" : form.vehicle_number, reference: form.reference,
        invoice_mode: isCredit ? "random" : form.invoice_mode, amount: splitsTotal(splits), pay_method: splits[0].method, splits,
        invoice_id: !isCredit && form.invoice_mode === "individual" ? (form.invoice_id || null) : null,
        invoice_no: !isCredit && form.invoice_mode === "individual" && !form.invoice_id ? (form.invoice_no || null) : null,
        cashier_signature, receiver_signature,
      });
      toast.success(`Receipt ${data.number} created`);
      if (files.length) await uploadAttachments("voucher", data.id, files);
      await downloadDocPdf(data.id, data.number);
      setOpen(false); setForm(blank()); setInvoices([]); setFiles([]);
      onDone && onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="open-receipt-btn" className={triggerClass || "h-11 px-5 rounded-md bg-card border border-input text-sm font-medium flex items-center gap-2 hover:bg-muted transition-colors"}>
          <ReceiptText size={16} /> {compact ? "Receipt" : "Payment Receipt"}
        </button>
      </DialogTrigger>
      <DialogContent className="bg-card max-h-[92vh] overflow-y-auto sm:max-w-lg" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader><DocTitle title="Payment Receipt" preview={preview} /></DialogHeader>
        <div className="space-y-3">
          {isCapital ? <CapitalPartnerPick form={form} setForm={setForm} caps={caps} />
            : isCredit ? <CreditAccountPicker open={open} form={form} setForm={setForm} accounts={accounts} setAccounts={setAccounts} />
              : <PayerFields form={form} setForm={setForm} />}
          <PartyTypeSection caps={caps} form={form} setForm={setForm} ptypes={ptypes} setPtypes={setPtypes} isCredit={isCredit} isCapital={isCapital} />
          {form.party_type && !isCredit && !isCapital && (
            <div>
              <label className={label}>Vehicle number</label>
              <Autocomplete field="vehicle" testid="receiptdoc-vehicle" value={form.vehicle_number} onChange={(v) => setForm({ ...form, vehicle_number: v })} placeholder="DL01AB1234" />
            </div>
          )}
          {!isCredit && !isCapital && <InvoiceModeSection form={form} setForm={setForm} invoices={invoices} fillFromInvoice={fillFromInvoice} />}
          <div>
            <label className={label}>Date</label>
            <input data-testid="receiptdoc-date" type="date" className={field + " num"} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <SplitRows rows={form.splits} setRows={(r) => setForm({ ...form, splits: r })} prefix="receiptdoc" partialOption
            target={!isCredit && !isCapital && form.invoice_mode === "individual" ? form.invoice_balance : 0} />
          <div>
            <label className={label}>Reference / description</label>
            <input data-testid="receiptdoc-ref" className={field} value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="optional" />
          </div>
          <AttachPicker files={files} setFiles={setFiles} testid="receiptdoc-attach" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
            <SignaturePad ref={cashierRef} label="Cashier signature" testid="receiptdoc-sign-cashier" />
            <SignaturePad ref={receiverRef} label="Receiver signature" testid="receiptdoc-sign-receiver" />
          </div>
          <p className="text-xs text-muted-foreground">Invoice payments reduce the party's credit ledger; any extra is kept on account. A signed PDF is generated.</p>
        </div>
        <DialogFooter>
          <button data-testid="receiptdoc-save" onClick={save} className="h-10 px-5 rounded-md bg-accent text-accent-foreground font-medium hover:bg-accent/90 transition-colors">Create &amp; download</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
